import { useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { differenceInYears, formatDate, isValid, parse } from 'date-fns';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import PortalHeading from '../../components/pages/customer-portal/heading.jsx';
import UserPlusIcon from '../../components/icons/user-plus.jsx';
import {
  turnstileStatusState,
  turnstileStatusStateStatuses,
  TurnstileWidget,
} from '../../components/turnstile-widget.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Card } from '../../components/ui/card.jsx';
import { Checkbox } from '../../components/ui/checkbox.jsx';
import { DateInputWithDatePicker } from '../../components/ui/date-picker.jsx';
import FormFooter from '../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form.jsx';
import { Input } from '../../components/ui/input.jsx';
import { msCatalystApi } from '../../services/api/ms-catalyst-api.js';
import {
  CAMPAIGN_TERMS_OF_DATA_SHARING,
  CAMPAIGN_TERMS_OF_DATA_SHARING_LIVLI_FAMILY,
  CAMPAIGN_TERMS_OF_PARTICIPATION,
} from '../../utils/constants.js';
import { formatDateToISO } from '../../utils/format.js';
import { REGEX_DATE_EU } from '../../utils/regex.js';

export const Register = () => {
  return (
    <Card className="p-4 sm:p-10">
      <div className="flex flex-col space-y-2 pb-4 text-left">
        <PortalHeading className="!text-xl">Criar conta</PortalHeading>
        <p className="text-sm text-muted-foreground">
          Introduza os dados abaixo para criar uma conta de cliente.
        </p>
      </div>

      <FormRegister />
    </Card>
  );
};

function FormRegister() {
  const navigate = useNavigate();
  const formRef = useRef();
  const turnstileRef = useRef();
  const { status: turnstileStatus, setStatus: setTurnstileStatus } =
    useSnapshot(turnstileStatusState);

  const createCustomerForm = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      birthDate: '',
      cc: '',
      email: '',
      phone: '',
      location: '',
      consentTermsOfParticipation: false,
      consentDataSharing: false,
      consentDataSharingLivliFamily: false,
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Nome é um campo obrigatório'),
        lastName: yup
          .string()
          .min(2, 'Apelido tem de ter pelo menos 2 caracteres')
          .required('Apelido é um campo obrigatório'),
        birthDate: yup
          .string()
          .required('Data de Nascimento é um campo obrigatório')
          .matches(
            REGEX_DATE_EU,
            'Data de Nascimento possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'dob',
            'Apenas clientes com 18 anos ou mais podem participar na campanha',
            function (value) {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());

              return differenceInYears(new Date(), parsedDate) >= 18;
            },
          ),
        cc: yup
          .string()
          .min(6, 'Nº Identificação tem de ter pelo menos 6 caracteres')
          .required('Nº Identificação é um campo obrigatório'),
        email: yup
          .string()
          .email('Email inserido não é válido')
          .required('Email é um campo obrigatório'),
        location: yup
          .string(true)
          .required('Localidade é um campo obrigatório'),
        consentTermsOfParticipation: yup
          .boolean(true)
          .oneOf(
            [true],
            '*Para criação de conta e participação na campanha, é necessário aceitar as Condições de participação',
          ),
      }),
    ),
  });

  const mutationCreateCustomer = useMutation({
    mutationFn: async (data) => {
      const { data: createUser } = await msCatalystApi.post('/auth/register', {
        turnstile_token: data.turnstileToken,
        user: {
          firstName: data.firstName,
          lastName: data.lastName,
          fullName: `${data.firstName} ${data.lastName}`,
          email: data.email,
          type: 'Customer',
        },
        customer: {
          firstName: data.firstName,
          lastName: data.lastName,
          fullName: `${data.firstName} ${data.lastName}`,
          email: data.email,
          cc: data.cc,
          phone: data.phone,
          location: data.location,
          consentTermsOfParticipation: data.consentTermsOfParticipation,
          consentDataSharing: data.consentDataSharing,
          consentDataSharingLivliFamily: data.consentDataSharingLivliFamily,
          birthDate: data.birthDate,
          role: 'Cliente',
          createdIn: 'Portal do Cliente',
        },
      });

      return createUser.data;
    },
  });

  const onFormSubmit = async (data) => {
    try {
      const formData = new FormData(formRef.current);
      const turnstileToken = formData.get('cf-turnstile-response');

      if (!turnstileToken) {
        toast.error('Erro ao realizar o login', {
          description:
            'Não foi possível determinar com sucesso a sua veracidade como humano',
        });

        return;
      }

      await mutationCreateCustomer.mutateAsync({
        ...data,
        birthDate: formatDateToISO(data.birthDate),
        turnstileToken,
      });

      toast.success(
        'Conta criada com sucesso! Verifique o seu email para concluír o processo.',
      );

      navigate('/');
    } catch (err) {
      setTurnstileStatus('pending');
      turnstileRef.current?.reset();

      toast.error('Erro ao criar conta cliente', {
        description:
          'Por favor tente novamente mais tarde ou verifique se já possui uma conta.',
      });
    }
  };

  return (
    <Form {...createCustomerForm}>
      <form
        ref={formRef}
        onSubmit={createCustomerForm.handleSubmit(onFormSubmit)}
      >
        {/* Fields */}
        <div className="mt-6 flex flex-col gap-6">
          <FormField
            control={createCustomerForm.control}
            name="email"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>Email</FormLabel>
                <FormControl>
                  <Input {...field} type="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createCustomerForm.control}
            name="firstName"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>Nome</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createCustomerForm.control}
            name="lastName"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>Apelido</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createCustomerForm.control}
            name="birthDate"
            render={({ field }) => {
              let selectedDate = null;

              if (field?.value?.match(REGEX_DATE_EU)) {
                const [day, month, year] = field.value.split('-');
                selectedDate = new Date(`${year}-${month}-${day}`);
              }

              return (
                <FormItem variant="portal">
                  <FormLabel required={true}>Data de nascimento</FormLabel>
                  <FormControl>
                    <DateInputWithDatePicker
                      input={{
                        ...field,
                        variant: 'portal',
                        onChange: (e) => {
                          const date = e.target.value;

                          if (!date && !isValid(new Date(date))) return;

                          field.onChange(date);
                        },
                      }}
                      calendar={{
                        selected: selectedDate,
                        onSelect: (date) => {
                          if (!date && !isValid(new Date(date))) return;

                          field.onChange(
                            formatDate(new Date(date), 'dd-MM-yyyy'),
                          );
                        },
                      }}
                    />
                  </FormControl>

                  <FormDescription>
                    Data de Nascimento tem de seguir o formato:{' '}
                    <i>dd-MM-yyyy</i>
                  </FormDescription>

                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={createCustomerForm.control}
            name="cc"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>
                  Nº de documento comprovativo de identidade
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>

                <FormDescription>
                  (Bilhete de Identidade, Cartão de Cidadão, Passaporte, Carta
                  de Condução ou Título de Residência)
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createCustomerForm.control}
            name="phone"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>Nº telemóvel</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createCustomerForm.control}
            name="location"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel required={true}>Localidade</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Consentimentos */}
        <div className="mt-10 flex flex-col gap-6">
          <FormField
            control={createCustomerForm.control}
            name="consentTermsOfParticipation"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel
                  className="!font-lively !text-base font-bold"
                  required={true}
                >
                  Condições de participação
                </FormLabel>
                <div className="flex min-h-10 flex-row items-center space-x-2.5">
                  <FormControl>
                    <Checkbox
                      className="size-6"
                      id="consentTermsOfParticipation"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  </FormControl>
                  <span className="text-xs text-portal-text">
                    {CAMPAIGN_TERMS_OF_PARTICIPATION}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createCustomerForm.control}
            name="consentDataSharing"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel className="!font-lively !text-base font-bold">
                  Comunicações de Marketing
                </FormLabel>
                <div className="flex min-h-10 flex-row items-center space-x-2.5">
                  <FormControl>
                    <Checkbox
                      className="size-6"
                      id="consentDataSharing"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  </FormControl>
                  <span className="text-xs text-portal-text">
                    {CAMPAIGN_TERMS_OF_DATA_SHARING}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createCustomerForm.control}
            name="consentDataSharingLivliFamily"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel className="!font-lively !text-base font-bold">
                  Adesão ao Programa de Vantagens Livli Family
                </FormLabel>
                <div className="flex min-h-10 flex-row items-center space-x-2.5">
                  <FormControl>
                    <Checkbox
                      className="size-6"
                      id="consentDataSharingLivliFamily"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  </FormControl>
                  <span className="text-xs text-portal-text">
                    {CAMPAIGN_TERMS_OF_DATA_SHARING_LIVLI_FAMILY}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="pt-6">
          <TurnstileWidget ref={turnstileRef} />
        </div>

        <FormFooter className="!pt-10">
          <Button
            type="submit"
            variant="portal"
            className="w-full"
            disabled={
              !createCustomerForm.formState.isDirty ||
              createCustomerForm.formState.isSubmitting ||
              turnstileStatus !== turnstileStatusStateStatuses.SOLVED
            }
            isLoading={createCustomerForm.formState.isSubmitting}
          >
            <UserPlusIcon />
            Criar conta
          </Button>
        </FormFooter>

        <div className="flex flex-col gap-y-1 pt-6">
          <Button
            asChild
            variant="link"
            type="button"
            className="h-auto text-left text-xs font-medium text-muted-foreground hover:opacity-75"
          >
            <Link to="/auth/login">Já possui uma conta? Entre aqui</Link>
          </Button>

          <Button
            asChild
            variant="link"
            type="button"
            className="h-auto text-left text-xs font-medium text-muted-foreground hover:opacity-75"
          >
            <Link to="/portal">
              Não pretende criar uma conta? Volte ao Portal de Campanhas
            </Link>
          </Button>
        </div>
      </form>
    </Form>
  );
}
